import React, { useState } from "react";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "src/components/ui/field";
import { Input } from "src/components/ui/input";
import { useIsXlScreen } from "~/lib/hooks/useScreen";
import { cn } from "~/lib/utils";
import { Textarea } from "../ui/textarea";
import {
  createFormHook,
  createFormHookContexts,
  revalidateLogic,
} from "@tanstack/react-form";
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadList,
  FileUploadTrigger,
} from "../ui/file-upload";
import { z } from "zod";
import { Upload, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { strapiRepository } from "~/strapi/strapiRepository";
import { toast } from "sonner";
import * as Sentry from "@sentry/tanstackstart-react";

function SubscribeButton({ label }: { label: string }) {
  const form = useFormContext();
  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <Button type="submit" disabled={isSubmitting}>
          {label}
        </Button>
      )}
    </form.Subscribe>
  );
}

function InputField({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  // The `Field` infers that it should have a `value` type of `string`
  const field = useFieldContext<string>();
  return (
    <Field className={className}>
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <Input
        id={field.name}
        name={field.name}
        aria-label={label}
        autoComplete={field.name}
        aria-invalid={field.state.meta.errors.length > 0}
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
      />
      <FieldError>{field.state.meta.errors?.[0]?.message ?? ""}</FieldError>
    </Field>
  );
}

function TextareaField({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  const field = useFieldContext<string>();
  return (
    <Field className={className}>
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <Textarea
        id={field.name}
        name={field.name}
        rows={6}
        aria-invalid={field.state.meta.errors.length > 0}
        aria-label={label}
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
      />
      <FieldError>{field.state.meta.errors?.[0]?.message ?? ""}</FieldError>
    </Field>
  );
}

const MAX_FILE_SIZE_IN_MB = 5;
const MAX_FILE_SIZE = MAX_FILE_SIZE_IN_MB * 1024 * 1024; // 5MB
const MAX_FILES = 3;

const { fieldContext, formContext, useFormContext, useFieldContext } =
  createFormHookContexts();
const { useAppForm } = createFormHook({
  fieldComponents: {
    InputField,
    TextareaField,
    FileUpload,
  },
  formComponents: {
    SubscribeButton,
  },
  fieldContext,
  formContext,
});

export const InquiryModal: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isXlScreen = useIsXlScreen();

  const { mutateAsync: postInquiry } = useMutation({
    mutationKey: ["post-inquiry"],
    mutationFn: async (data: typeof form.state.values) => {
      return await strapiRepository.postInquiry(data);
    },
    onError: (error) => {
      toast.error(
        "Nepodařilo se odeslat poptávku. Zkuste to prosím znovu později. Error: " +
          (error instanceof Error ? error.message : "Unknown error"),
      );
      console.error("Error posting inquiry:", error);
      Sentry.captureException(error);
    },
  });

  const form = useAppForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
      files: [] as File[],
    },
    validators: {
      onDynamic: z.object({
        name: z.string().min(1, "Jméno je povinné"),
        email: z.email("Neplatná emailová adresa"),
        phone: z.string(),
        message: z.string().min(1, "Zpráva je povinná"),
        files: z
          .array(
            z
              .file()
              .max(
                MAX_FILE_SIZE,
                `Maximální velikost souboru je ${MAX_FILE_SIZE_IN_MB}MB`,
              ),
          )
          .max(MAX_FILES, `Maximálně lze nahrát ${MAX_FILES} soubory`),
      }),
    },
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    onSubmit: async ({ value }) => {
      const result = await postInquiry(value);
      setIsDialogOpen(false);
      form.reset();
      toast.success(
        "Poptávka byla odeslána. Ozveme se vám co nejdříve. Děkujeme!",
      );
      return result;
    },
    onSubmitInvalid() {
      const InvalidInput = document.querySelector(
        '[aria-invalid="true"]',
      ) as HTMLInputElement;

      console.log("InvalidInput", InvalidInput);

      InvalidInput?.focus();
    },
  });

  return (
    <Dialog
      open={isDialogOpen}
      onOpenChange={(open) => {
        if (!open) {
          form.reset();
        }
        setIsDialogOpen(open);
      }}
    >
      <Button
        asChild
        variant={isXlScreen === undefined || isXlScreen ? "default" : "link"}
        className={cn(
          "h-auto w-full justify-start text-lg font-semibold tracking-wide uppercase xl:ml-4",
        )}
      >
        <DialogTrigger>
          <li className="w-full py-2 text-start md:pl-4 xl:py-0 xl:pl-0">
            Rychlá poptávka
          </li>
        </DialogTrigger>
      </Button>
      <DialogContent
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <form
          className="flex flex-col gap-4"
          id="formular"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <DialogHeader>
            <DialogTitle>Rychlá poptávka</DialogTitle>
            <DialogDescription>
              Popište nám, co potřebujete, a my se vám co nejdříve ozveme. Nebo
              nám napište na email{" "}
              <span className="underline">vojtech.bobek@vesas.cz</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="col-span2">
            <form.AppField name="name">
              {(field) => <field.InputField label="Jméno *" />}
            </form.AppField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <form.AppField name="email">
              {(field) => (
                <field.InputField
                  label="Email *"
                  className="col-span-2 md:col-span-1"
                />
              )}
            </form.AppField>
            <form.AppField name="phone">
              {(field) => (
                <field.InputField
                  label="Telefon"
                  className="col-span-2 md:col-span-1"
                />
              )}
            </form.AppField>
            <form.AppField name="message">
              {(field) => (
                <field.TextareaField className="col-span-2" label="Zpráva *" />
              )}
            </form.AppField>

            <form.AppField name="files">
              {(field) => (
                <Field className="col-span-2">
                  <FieldLabel htmlFor={field.name}>Přílohy</FieldLabel>
                  <field.FileUpload
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onValueChange={(files) => {
                      field.handleChange(files);
                    }}
                    onFileReject={(e) => {
                      if (e.size > MAX_FILE_SIZE) {
                        toast.error(
                          `Soubor ${e.name} je příliš velký. Maximální velikost je ${MAX_FILE_SIZE_IN_MB}MB.`,
                        );
                      } else if (form.state.values.files.length >= MAX_FILES) {
                        toast.error(
                          `Nelze přidat soubor ${e.name}. Maximální počet souborů je ${MAX_FILES}.`,
                        );
                      } else {
                        toast.error(`Nepodařilo se nahrát soubor: ${e.name}`);
                      }
                    }}
                    multiple
                    accept=".jpg,.JPG,.JPEG,.jpeg,.png,.pdf,.doc,.docx,.zip,.xls,.xlsx"
                    maxSize={MAX_FILE_SIZE}
                    maxFiles={MAX_FILES}
                  >
                    {form.state.values.files.length < MAX_FILES && (
                      <FileUploadDropzone>
                        <div className="flex flex-col items-center gap-1 text-center">
                          <div className="flex items-center justify-center">
                            <Upload className="text-muted-foreground size-6" />
                          </div>
                          <p className="text-sm font-medium">
                            Přetáhněte soubory zde
                          </p>
                          <p className="text-muted-foreground text-xs">
                            Nebo klikněte pro výběr (max {MAX_FILES} soubory, do{" "}
                            {MAX_FILE_SIZE_IN_MB}MB každý)
                          </p>
                        </div>
                        <FileUploadTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 w-fit"
                          >
                            Procházet soubory
                          </Button>
                        </FileUploadTrigger>
                      </FileUploadDropzone>
                    )}
                    <FileUploadList>
                      {field.state.value.map((file, index) => (
                        <FileUploadItem key={index} value={file}>
                          <FileUploadItemPreview />
                          <FileUploadItemMetadata />
                          <FileUploadItemDelete
                            asChild
                            onClick={() => {
                              const newFiles = [...field.state.value];
                              newFiles.splice(index, 1);
                              field.handleChange(newFiles);
                            }}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7"
                              aria-label="Odstranit soubor"
                            >
                              <X />
                            </Button>
                          </FileUploadItemDelete>
                        </FileUploadItem>
                      ))}
                    </FileUploadList>
                  </field.FileUpload>
                  <FieldError>
                    {field.state.meta.errors?.[0]?.message ?? ""}
                  </FieldError>
                </Field>
              )}
            </form.AppField>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Zrušit</Button>
            </DialogClose>
            <form.AppForm>
              <form.SubscribeButton label="Odeslat" />
            </form.AppForm>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
